import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { useSearchParams } from 'next/navigation';
import { useCookie } from 'next-cookie';
import Head from 'next/head';
import { Provider, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import classNames from 'classnames';
import { useViewport } from '@forma/forma-ui-kit';
import { ThemeProvider } from '@forma/forma-ui-theme';
import { useQuery } from '@/hooks/useQuery';
import useClient from '@/hooks/useClient';
import baseApi from '@/helpers/baseApi';
import store from '@/store';

import '@forma/forma-ui-kit/dist/style.css';
import '@forma/forma-filling-kit/dist/style.css';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import TopPanel from '@/components/TopPanel';
import ClientWrap from '@/components/ClientWrap';
import CookieNotification from '@/components/CookieNotification';
import NotificationsWrapper from '@/components/NotificationsWrapper';
import ErrorModal from '@/components/ErrorModal';
import WhiteLabelTheme from '@/components/WhiteLabelTheme';
import ErrorBoundary from '@/components/ErrorBoundary';
import { breakpoints } from '@/breakpoints';
import useClientI18next from '@/hooks/useClientI18next';
import useSlowInternetNotification from '@/hooks/useSlowInternetNotification';

import { mainDataAtom, layoutAtom } from '@/store/common';
import { WhiteLabelLogosContext, WhiteLabelNameContext } from '@/hooks/useWhiteLabelLogos';
import { TrialDaysContext } from '@/hooks/useTrialDays';
import { i18nConfig, WHITE_LABEL_NAME_OPTION } from '@/helpers/whiteLabelPostProcessor';

import 'rc-slider/assets/index.css';

import '@/styles/fonts.css';
import '@/styles/globals.css';
import '@/styles/slider.css';
import '@/styles/slick-slider.css';

import 'slick-slider/slick/slick.css';
import 'slick-slider/slick/slick-theme.css';

const siteUrl = process.env.SITE_URL;
const apiUrl = process.env.API_URL;
const isProduction = process.env.NODE_ENV === 'production';

const emptyLogos = {};

const utmSkippingValues = [
  'locale', '__nextNotFoundSrcPage'
];

// const MergeStore = ({ store, mainData, layout }) => {
//   useHydrateAtoms([[mainDataAtom, mainData], [layoutAtom, layout], { store }]);
//   return null;
// };

const App = ({ Component, pageProps }) => {
  const { i18n } = useTranslation();
  const { initialState, headersParse, whiteLabel, ...restPageProps } = pageProps;
  const { pathname } = useRouter();
  const cookie = useCookie();
  const searchParams = useSearchParams();
  const viewport = useViewport(breakpoints);
  const isClient = useClient();
  useSlowInternetNotification({ store });

  const lang = i18n.resolvedLanguage ?? i18n.language ?? headersParse?.language;

  // the name of the product of the sender workspace instead of DataDoc in the texts of this page
  // only the public pages bring the theme in the props; the CRM widgets set the name themselves after the load of the theme (CrmLayout)
  if (i18n?.options && whiteLabel) i18n.options[WHITE_LABEL_NAME_OPTION] = whiteLabel.enabled ? whiteLabel.productName : undefined;

  useEffect(() => {
    if (lang) baseApi.setOptions({ headers: { 'X-Language': lang } });
  }, [lang]);

  const { data: ls } = useQuery(useCallback(() => {
    const layout = localStorage.getItem('layout');
    const token = localStorage.getItem('formaToken');

    if (token) baseApi.setOptions({ headers: { 'Authorization': `Bearer ${token}` } });

    return { token, layout };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]), { key: 'ls' });

  const themeData = {
    viewport: isClient ? viewport : headersParse?.type,
    currencies: initialState?.main?.currencies ?? {},
    isMenuOpen: false,
    apiUrl: apiUrl ?? '',
    lang,
    token: ls?.token,
  };

  useEffect(() => {
    const date = new Date();
    const utm = cookie.get('utm') ?? {};
    for (const [key, value] of searchParams.entries()) {
      if (!utmSkippingValues.includes(key)) utm[key] = value;
    }
    if (headersParse?.referrer) {
      utm.referrer = headersParse.referrer;
    }
    cookie.set('utm', JSON.stringify(utm), {
      path: '/',
      expires: new Date(date.setDate(date.getDate() + 7)),
      domain: isProduction ? siteUrl.replace('https://', '.') : null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const layout = ls?.layout;
  const setLayout = useSetAtom(layoutAtom, { store });

  useEffect(() => {
    if (layout) setLayout(layout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  useClientI18next();
  useHydrateAtoms([[mainDataAtom, initialState?.main]], { store });

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>
      <WhiteLabelTheme theme={whiteLabel} />
      <Provider store={store}>
        {/* <MergeStore store={store} mainData={initialState?.main} layout={ls?.layout} />*/}
        <ErrorBoundary>
          <ThemeProvider value={themeData}>
            <WhiteLabelLogosContext.Provider value={whiteLabel?.enabled ? whiteLabel.logos : emptyLogos}>
            <WhiteLabelNameContext.Provider value={whiteLabel?.enabled ? whiteLabel.productName : undefined}>
            <TrialDaysContext.Provider value={initialState?.main?.trialDays}>
            <div id="page" className={Component.pageClassName}>
              {Component.topPanel ? Component.topPanel() : <TopPanel />}
              {Component.header ? Component.header() : <Header />}

              <NotificationsWrapper />
              {!Component.hideCookieNotification && <CookieNotification />}
              <ClientWrap>
                <ErrorModal />
              </ClientWrap>

              <main className={classNames('main-container content', Component.contentClassName)}>
                <Component {...restPageProps} />
              </main>

              {Component.footer ? Component.footer() : <Footer />}
            </div>
            </TrialDaysContext.Provider>
            </WhiteLabelNameContext.Provider>
            </WhiteLabelLogosContext.Provider>
          </ThemeProvider>
        </ErrorBoundary>
      </Provider>
    </>
  );
};

// The i18n override of "White Label" (the name of the product in the texts) is applied only to the pages that bring translations (_nextI18Next).
// A page without translations (e.g. /csrf, which gives the security key to the LK) has to stay as it was: with the override
// next-i18next creates an instance without resources, the page suspends forever and its effects never run.
const AppWithWhiteLabelI18n = appWithTranslation(App, i18nConfig);
const AppWithI18n = appWithTranslation(App);

const Root = (props) => (props.pageProps?._nextI18Next ? <AppWithWhiteLabelI18n {...props} /> : <AppWithI18n {...props} />);

export default Root;
